import { useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { useCreateCustomTalkingPoint } from "@acto/taskpane/core/data-access/custom-talking-points";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";

const Root = styled(Pill)(({ theme }) => ({
  width: "100%",
  boxShadow: theme.fieldSalesTwo.shadows.glass,
}));

const TextInput = styled("input")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": {
    color: theme.fieldSalesTwo.colors.neutral[700],
  },
}));

interface CustomTalkingPointInputProps {
  clientId: string;
}

export function CustomTalkingPointInput({ clientId }: CustomTalkingPointInputProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.customTalkingPoint");
  const [title, setTitle] = useState("");
  const { createCustomTalkingPoint, result } = useCreateCustomTalkingPoint();

  const submit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || result.fetching) return;

    // Client-generated id so the optimistic cache entity and the server row are the same entity.
    const created = await createCustomTalkingPoint({ id: crypto.randomUUID(), clientId, title: trimmedTitle });
    if (created) {
      setTitle("");
    }
  };

  return (
    <Root
      as="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        void submit();
      }}
    >
      <TextInput
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder={t("inputPlaceholder")}
        aria-label={t("inputLabel")}
        enterKeyHint="done"
      />
    </Root>
  );
}
